<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, onBeforeMount, ref } from 'vue'
import { notify } from '@kyvg/vue3-notification'

import useForm from '@/hooks/use-form'
import { FieldPreference } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import {
    DeliveryDescriptionMatch,
    DeliveryManagementPreferences,
    TourRoutingMode,
} from '@/types/delivery-management'
import { newMoneyFormatter } from '@/utils/numbers'

import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'

const authStore = useAuthStore()
const { t } = useI18n()

const form = useForm<DeliveryManagementPreferences>({
    shipmentPodCreation: false,
    colliScan: FieldPreference.Optional,
    images: FieldPreference.Optional,
    note: FieldPreference.Optional,
    transaction: FieldPreference.Optional,
    signature: FieldPreference.Optional,
    unsuccessful: FieldPreference.Optional,
    deviation: FieldPreference.Optional,
    hideReturnShipmentColliScan: false,
    serviceTimePerStop: 0,
    bulkScanningThreshold: 0,
    shipmentDelayAlertThreshold: 0,
    tourDriverConfirmation: false,
    multipleDriversOnTour: false,
    deliveryDescriptionMatch: DeliveryDescriptionMatch.Location,
    tourRoutingMode: TourRoutingMode.Shortest,
    consolidateEvents: false,
    mileageCost: null,
    showNavigateHomeButton: false,
    scanShipmentsOntoTour: false,
    calculateEtaButtonInApp: false,
    optimizeTourButtonInApp: false,
    startTransactionOnIsolatedShipmentScan: false,
    skipTransitOnIsolatedShipmentScan: false,
    shipperAsSenderOnTransactions: false,
    showFullCompanyInformationOnShipment: false,
})
const bulkScanningEnabled = ref(false)

const hasRoutingLicenses = computed(() =>
    authStore.hasLicense([
        LicenseType.DeliveryManagementNavigation,
        LicenseType.DeliveryManagementTourETA,
        LicenseType.DeliveryManagementTourOptimization,
    ]),
)

const currencyCode = computed(() => {
    const moneyFormatter = newMoneyFormatter(authStore.company.options.currency || 'USD')

    // We have no way to easily get the symbol from the NumberFormat
    // so we'll have to do it like this
    return moneyFormatter.format(0).replace('0', '').trim()
})

async function savePreferences() {
    const response = await form.submit(
        'PUT',
        window.route('dm.company.preferences.update', { company: authStore.companyId }),
    )

    if (response) {
        authStore.licenses[LicenseType.DeliveryManagementModule] = {
            ...authStore.licenses[LicenseType.DeliveryManagementModule],
            ...form.data,
        }

        notify({
            title: t('licensePreferencesUpdated'),
            type: 'success',
        })
    }
}

function toggleBulkScanningThreshold() {
    if (bulkScanningEnabled.value) {
        form.data.bulkScanningThreshold = 50
    } else {
        form.data.bulkScanningThreshold = 0
    }
}

function setBulkScanningThreshold(value: string | number | null) {
    form.data.bulkScanningThreshold = Math.max(parseInt(value + '', 10), 0)
}

onBeforeMount(() => {
    const prefs: Partial<DeliveryManagementPreferences> =
        authStore.licenses[LicenseType.DeliveryManagementModule] ?? {}

    form.reset({ ...prefs })

    bulkScanningEnabled.value = form.data.bulkScanningThreshold > 0
})
</script>

<template>
    <MyForm
        class="grid grid-cols-2 lg:flex-row gap-6"
        :errors="form.errors.value"
        @submit.prevent="savePreferences"
    >
        <div class="space-y-2">
            <h3 class="text-sm font-bold uppercase text-primary-400 dark:text-primary-300"></h3>

            <MyRadioButtonGroup
                v-model="form.data.shipmentPodCreation"
                class="relative"
                :label="t('podCreation')"
            >
                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />

                <template #label>
                    {{ t('podCreation') }}
                    <mdi:information
                        v-tooltip="t('podCreationExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.multipleDriversOnTour"
                class="relative"
                :label="t('multipleDriversOnTour')"
            >
                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />

                <template #label>
                    {{ t('multipleDriversOnTour') }}
                    <mdi:information
                        v-tooltip="t('multipleDriversOnTourExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.consolidateEvents"
                class="relative"
                :label="t('consolidateShipments')"
            >
                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />

                <template #label>
                    {{ t('consolidateShipments') }}
                    <mdi:information
                        v-tooltip="t('consolidateShipmentsExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.colliScan" :label="t('colliScan')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.hideReturnShipmentColliScan"
                class="relative"
                :label="t('returnShipmentColliScan')"
            >
                <MyRadioButton :label="t('visible')" :value="false" />
                <MyRadioButton :label="t('hidden')" :value="true" />

                <template #label>
                    {{ t('returnShipmentColliScan') }}
                    <mdi:information
                        v-tooltip="t('returnShipmentColliScanExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.images" :label="t('images')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.note" :label="t('note')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.transaction" :label="t('packagingTransaction')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup v-model="form.data.scanShipmentsOntoTour" class="relative">
                <template #label>
                    {{ t('scanShipmentsOntoTour') }}
                    <mdi:information
                        v-tooltip="t('scanShipmentsOntoTourExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>

                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.IsolatedShipmentScanning)"
                v-model="form.data.startTransactionOnIsolatedShipmentScan"
                class="relative"
            >
                <template #label>
                    {{ t('startTransactionOnIsolatedShipmentScan') }}
                    <mdi:information
                        v-tooltip="t('startTransactionOnIsolatedShipmentScanExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>

                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.IsolatedShipmentScanning)"
                v-model="form.data.skipTransitOnIsolatedShipmentScan"
                class="relative"
            >
                <template #label>
                    {{ t('skipTransitOnIsolatedShipmentScan') }}
                    <mdi:information
                        v-tooltip="t('skipTransitOnIsolatedShipmentScanExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>

                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.PackagingModule)"
                v-model="form.data.shipperAsSenderOnTransactions"
                class="relative"
            >
                <template #label>
                    {{ t('shipperAsSenderOnTransactions') }}
                    <mdi:information
                        v-tooltip="t('shipperAsSenderOnTransactionsExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>

                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />
            </MyRadioButtonGroup>
        </div>

        <!-- Right column -->
        <div class="space-y-2">
            <h3 class="text-sm font-bold uppercase text-primary-400 dark:text-primary-300"></h3>

            <MyRadioButtonGroup v-model="form.data.signature" :label="t('signature')">
                <MyRadioButton :label="t('required')" :value="FieldPreference.Required" />
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.unsuccessful"
                class="relative"
                :label="t('unsuccessful')"
            >
                <MyRadioButton :label="t('optional')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />

                <template #label>
                    {{ t('unsuccessfulShipment') }}
                    <mdi:information
                        v-tooltip="t('unsuccessfulExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-model="form.data.deviation"
                class="relative"
                :label="t('deviation')"
            >
                <MyRadioButton :label="t('visible')" :value="FieldPreference.Optional" />
                <MyRadioButton :label="t('hidden')" :value="FieldPreference.Hidden" />

                <template #label>
                    {{ t('deviations') }}
                    <mdi:information
                        v-tooltip="t('deviationsExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <template
                v-if="
                    authStore.hasLicense(LicenseType.DeliveryManagementTourOptimization) ||
                    authStore.hasLicense(LicenseType.DeliveryManagementTourETA)
                "
            >
                <MyInput
                    v-model="form.data.serviceTimePerStop"
                    name="serviceTimePerStop"
                    type="number"
                    :label="t('serviceTimePerStop')"
                />
                <MyInput
                    v-model="form.data.shipmentDelayAlertThreshold"
                    name="shipmentDelayAlertThreshold"
                    type="number"
                    :label="t('shipmentDelayAlertThreshold')"
                >
                    <template #label="{ id, label }">
                        <div class="flex justify-between">
                            <MyInputLabel :for="id" v-text="label" />
                            <mdi:information
                                v-tooltip="t('shipmentDelayAlertThresholdDescription')"
                                class="h-4 w-4"
                            />
                        </div>
                    </template>
                </MyInput>
            </template>

            <div>
                <div class="flex items-center space-x-2">
                    <MyCheckbox
                        v-model="bulkScanningEnabled"
                        :label="t('bulkScanningThreshold')"
                        @change="toggleBulkScanningThreshold"
                    />
                    <mdi:information v-tooltip="t('bulkScanningThresholdExplanation')" />
                </div>
                <MyInput
                    v-if="bulkScanningEnabled"
                    :value="form.data.bulkScanningThreshold"
                    name="bulkScanningThreshold"
                    type="number"
                    :min="1"
                    :label="t('threshold')"
                    @update:model-value="setBulkScanningThreshold"
                />
            </div>

            <div class="flex items-center space-x-2">
                <MyCheckbox
                    v-model="form.data.tourDriverConfirmation"
                    :label="t('tourDriverConfirmation')"
                />
                <mdi:information v-tooltip="t('tourDriverConfirmationExplanation')" />
            </div>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.ShipmentDeliveryDescriptions)"
                v-model="form.data.deliveryDescriptionMatch"
                class="relative"
            >
                <MyRadioButton
                    :label="t('byLocation')"
                    :value="DeliveryDescriptionMatch.Location"
                />
                <MyRadioButton
                    :label="t('byShipmentName')"
                    :value="DeliveryDescriptionMatch.ShipmentName"
                />

                <template #label>
                    {{ t('deliveryDescriptionMatch') }}
                    <mdi:information
                        v-tooltip="t('deliveryDescriptionMatchExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="hasRoutingLicenses"
                v-model="form.data.tourRoutingMode"
                class="relative"
            >
                <MyRadioButton :label="t('shortest')" :value="TourRoutingMode.Shortest" />
                <MyRadioButton :label="t('fastest')" :value="TourRoutingMode.Fastest" />

                <template #label>
                    {{ t('routingMode') }}
                    <mdi:information
                        v-tooltip="t('routingModeExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.DeliveryManagementTourETA)"
                v-model="form.data.calculateEtaButtonInApp"
                class="relative"
            >
                <MyRadioButton :label="t('visible')" :value="true" />
                <MyRadioButton :label="t('hidden')" :value="false" />

                <template #label>
                    {{ t('calculateEtaButtonInApp') }}
                    <mdi:information
                        v-tooltip="t('calculateEtaButtonInAppExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.DeliveryManagementTourOptimization)"
                v-model="form.data.optimizeTourButtonInApp"
                class="relative"
            >
                <MyRadioButton :label="t('visible')" :value="true" />
                <MyRadioButton :label="t('hidden')" :value="false" />

                <template #label>
                    {{ t('optimizeTourButtonInApp') }}
                    <mdi:information
                        v-tooltip="t('optimizeTourButtonInAppExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>
            </MyRadioButtonGroup>

            <div v-if="authStore.hasLicense(LicenseType.ShipmentCostCalculation)" class="mt-3 mb-6">
                <hr class="mb-2" />

                <div class="flex items-center space-x-3 align-middle relative">
                    <MyInput
                        v-model.number="form.data.mileageCost"
                        :label="t('mileageCost')"
                        :placeholder="t('mileageCost')"
                        type="number"
                        name="mileageCost"
                    >
                        <template #label="{ label }">
                            <MyInputLabel>
                                <div class="font-semibold">
                                    {{ label }}
                                </div>
                                <mdi:information
                                    v-tooltip="t('mileageCostExplanation')"
                                    class="absolute top-1 right-0"
                                />
                            </MyInputLabel>
                        </template>
                        <template v-if="currencyCode" #button>
                            <span
                                class="absolute top-0 bottom-0.5 right-2 m-auto h-5"
                                v-text="`${currencyCode}/${t('km')}`"
                            />
                        </template>
                    </MyInput>
                </div>
            </div>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.DeliveryManagementNavigation)"
                v-model="form.data.showNavigateHomeButton"
                class="relative"
            >
                <template #label>
                    {{ t('showNavigateHomeButton') }}
                    <mdi:information
                        v-tooltip="t('showNavigateHomeButtonExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>

                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />
            </MyRadioButtonGroup>

            <MyRadioButtonGroup
                v-if="authStore.hasLicense(LicenseType.DeliveryManagementNavigation)"
                v-model="form.data.showFullCompanyInformationOnShipment"
                class="relative"
            >
                <template #label>
                    {{ t('showFullCompanyInformationOnShipment') }}
                    <mdi:information
                        v-tooltip="t('showFullCompanyInformationOnShipmentExplanation')"
                        class="absolute top-1 right-0"
                    />
                </template>

                <MyRadioButton :label="t('enabled')" :value="true" />
                <MyRadioButton :label="t('disabled')" :value="false" />
            </MyRadioButtonGroup>
        </div>

        <div class="col-span-2 flex justify-end pt-5">
            <MyButton
                type="submit"
                scheme="primary"
                :loading="form.loading.value"
                :disabled="form.loading.value"
            >
                {{ t('save') }}
            </MyButton>
        </div>
    </MyForm>
</template>
